import * as React from 'react'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import { Autocomplete, Checkbox, CircularProgress, Stack, Typography, useTheme } from '@mui/material'
import { Order, HeadCell } from '../../../utils/tableFunctions'
import TableButtonDelete from '../../table/TableButtonDelete'
import EnhancedTableHead from '../../table/EnhancedTableHead'
import CustomTextField from '../../common/CustomTextField'
import { ContractorBriefDto, ContractorOfEventDto, ContractorType } from '../../../services/api/sharedTypes/Contractor'
import { EventDto } from '../../../services/api/sharedTypes/Event'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import {
    convertContractorOfEventDtoToContractorBriefDto,
    findNameOfContractorAssignedToDifferentEventById,
} from '../../../utils/contractor-utils'
import MarkChatReadRoundedIcon from '@mui/icons-material/MarkChatReadRounded'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import MonetizationOnRoundedIcon from '@mui/icons-material/MonetizationOnRounded'
import CustomTableCell from '../../common/CustomTableCell'
import { IdNameDto } from '../../../services/api/sharedTypes/Common'
import SmallPlusButton from '../../common/SmallPlusButton'

const headCells: readonly HeadCell[] = [
    {
        id: 'contractorName',
        numeric: false,
        disablePadding: false,
        label: 'Brigádník',
    },
    {
        id: 'costs',
        numeric: true,
        disablePadding: false,
        label: 'Náklady',
    },
    {
        id: 'isLeader',
        numeric: false,
        disablePadding: false,
        label: 'Leader',
        centered: true,
    },
    {
        id: 'hasReacted',
        numeric: false,
        disablePadding: false,
        label: 'Brigádník reagoval',
        component: <MarkChatReadRoundedIcon />,
        centered: true,
    },
    {
        id: 'isConfirmed',
        numeric: false,
        disablePadding: false,
        label: 'Potvrzení brigádníka',
        component: <CheckCircleOutlineRoundedIcon />,
        centered: true,
    },
    {
        id: 'gotPaid',
        numeric: false,
        disablePadding: false,
        label: 'Dostal zaplaceno',
        component: <MonetizationOnRoundedIcon />,
        centered: true,
    },
    {
        id: 'buttons',
        numeric: false,
        disablePadding: false,
        label: '',
    },
]
interface BrigadierTableProps {
    contractors: IdNameDto[]
    contractorList: ContractorBriefDto[]
    isContractorListLoading: boolean
    handleSelectContractorChange: (
        newValue: IdNameDto,
        oldId: string,
        index: number,
        sortBy: keyof ContractorOfEventDto | undefined,
        sortOrder: 'asc' | 'desc'
    ) => void
    event: EventDto
    setEvent: (value: React.SetStateAction<EventDto | undefined>) => void
    /**
     * onValueChanged is function called when the user changes some value in the table
     * @param value true if contractor was changed, false otherwise
     */
    onValueChanged?: (value: React.SetStateAction<boolean>) => void
}

const BrigadierTable: React.FC<BrigadierTableProps> = ({
    contractors,
    event,
    contractorList,
    isContractorListLoading,
    setEvent,
    handleSelectContractorChange,
    onValueChanged,
}: BrigadierTableProps) => {
    const theme = useTheme()

    const [order, setOrder] = React.useState<Order>('desc')
    const [orderBy, setOrderBy] = React.useState<keyof ContractorOfEventDto>()

    const brigadiers = event.contractors
        .filter((contractor) => contractor.contractorType !== ContractorType.Carrier ?? [])
        .sort((a: ContractorOfEventDto, b: ContractorOfEventDto) => {
            if (orderBy) {
                if ((a[orderBy] ?? 0) < (b[orderBy] ?? 0)) {
                    return order === 'asc' ? -1 : 1
                }
                if ((a[orderBy] ?? 0) > (b[orderBy] ?? 0)) {
                    return order === 'asc' ? 1 : -1
                }
            }
            return 0
        })

    const carriers = event.contractors.filter((contractor) => contractor.contractorType === ContractorType.Carrier ?? [])

    const brigadiersInDifferentEvent = React.useMemo(
        () =>
            brigadiers
                .map((contractor) => {
                    return contractor.contractorId
                        ? findNameOfContractorAssignedToDifferentEventById(
                            contractor.contractorId,
                            event.id,
                            event.startsAt,
                            event.endsAt,
                            contractorList
                        )
                        : null
                })
                .filter((contractor) => contractor !== null),
        [brigadiers, event.id, event.startsAt, event.endsAt, contractorList]
    )

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property as keyof ContractorOfEventDto)
    }

    return (
        <>
            <Stack direction='row' justifyContent='space-between' mb={2}>
                <Typography variant='h6' color='primary'>
                    Brigádníci
                </Typography>
                <SmallPlusButton onClick={() => {
                    setEvent({
                        ...event,
                        contractors: [
                            ...brigadiers,
                            {
                                costs: 0,
                                isLeader: false,
                                hasResponded: false,
                                isConfirmed: false,
                                gotPaid: false,
                                contractorName: '',
                                contractorType: ContractorType.Brigadier,
                            },
                            ...carriers,
                        ],
                    })
                }} />
            </Stack>
            <Box style={{ width: '100%' }}>
                <Stack direction='column' gap={1}>
                    <TableContainer>
                        <Table size='small'>
                            <EnhancedTableHead
                                order={order}
                                orderBy={orderBy ?? ''}
                                onRequestSort={handleRequestSort}
                                rowCount={brigadiers.length}
                                headCells={headCells}
                            />
                            <TableBody>
                                {brigadiers.map((row, index: number) => {
                                    return (
                                        <TableRow
                                            hover
                                            tabIndex={-1}
                                            key={index}
                                            style={{
                                                height: '42px',
                                                backgroundColor: row.isConfirmed ? theme.palette.tableRow?.background.paid : '',
                                            }}
                                        >
                                            <CustomTableCell>
                                                {contractors ? (
                                                    <Autocomplete
                                                        options={contractors
                                                            .filter(
                                                                (contractor) =>
                                                                    !brigadiers.some(
                                                                        (localContractor) =>
                                                                            localContractor.contractorId === contractor.id
                                                                    )
                                                            )
                                                            .sort((a, b) => {
                                                                if (a.name > b.name) return 1
                                                                if (a.name < b.name) return -1
                                                                return 0
                                                            })}
                                                        getOptionLabel={(option) =>
                                                            option.name === 'Null' ? '' : option.name
                                                        }
                                                        value={
                                                            contractors.find(
                                                                (contractor) => contractor.id === row.contractorId
                                                            ) ??
                                                            convertContractorOfEventDtoToContractorBriefDto(brigadiers[index]) ??
                                                            null
                                                        }
                                                        size='small'
                                                        onChange={(eventChange, newValue) => {
                                                            if (newValue === null) return
                                                            handleSelectContractorChange(
                                                                newValue,
                                                                row.contractorId ?? '',
                                                                index,
                                                                orderBy,
                                                                order
                                                            )
                                                            onValueChanged && onValueChanged(true)
                                                        }}
                                                        renderInput={(params) => (
                                                            <CustomTextField
                                                                {...params}
                                                                size='small'
                                                                sx={{ minWidth: '170px' }}
                                                                isSmall
                                                                textSize={14}
                                                                isPaddingDisabled
                                                            />
                                                        )}
                                                    />
                                                ) : (
                                                    <Typography variant='body1' color='error'>
                                                        Chyba při načítání brigádníků
                                                    </Typography>
                                                )}
                                            </CustomTableCell>
                                            <CustomTableCell width={84}>
                                                <CustomTextField
                                                    value={row.costs || ''}
                                                    size='small'
                                                    isSmall
                                                    style={{ width: '69px' }}
                                                    onChange={(e) => {
                                                        if (isNaN(Number(e.target.value))) return
                                                        onValueChanged && onValueChanged(true)
                                                        setEvent({
                                                            ...event,
                                                            contractors: [
                                                                ...brigadiers.map((contractor, i) => {
                                                                    if (index === i) {
                                                                        return {
                                                                            ...contractor,
                                                                            costs: Number(e.target.value),
                                                                        }
                                                                    }
                                                                    return contractor
                                                                }),
                                                                ...carriers,
                                                            ],
                                                        })
                                                    }}
                                                />
                                            </CustomTableCell>
                                            <CustomTableCell center width={70}>
                                                <Checkbox
                                                    checked={row.isLeader ? true : false}
                                                    onChange={(e) => {
                                                        setEvent({
                                                            ...event,
                                                            contractors: [
                                                                ...brigadiers.map((contractor, i) => {
                                                                    if (index === i) {
                                                                        return {
                                                                            ...contractor,
                                                                            isLeader: e.target.checked,
                                                                        }
                                                                    }
                                                                    return contractor
                                                                }),
                                                                ...carriers,
                                                            ],
                                                        })
                                                        onValueChanged && onValueChanged(true)
                                                    }}
                                                />
                                            </CustomTableCell>
                                            <CustomTableCell width={70} center>
                                                <Checkbox disabled checked={row.hasResponded ? true : false} />
                                            </CustomTableCell>
                                            <CustomTableCell width={70} center>
                                                <Checkbox
                                                    checked={row.isConfirmed ? true : false}
                                                    onChange={(e) => {
                                                        setEvent({
                                                            ...event,
                                                            contractors: [
                                                                ...brigadiers.map((contractor, i) => {
                                                                    if (index === i) {
                                                                        return {
                                                                            ...contractor,
                                                                            isConfirmed: e.target.checked,
                                                                        }
                                                                    }
                                                                    return contractor
                                                                }),
                                                                ...carriers,
                                                            ],
                                                        })
                                                        onValueChanged && onValueChanged(true)
                                                    }}
                                                />
                                            </CustomTableCell>
                                            <CustomTableCell width={70} center>
                                                <Checkbox
                                                    checked={row.gotPaid ? true : false}
                                                    onChange={(e) => {
                                                        setEvent({
                                                            ...event,
                                                            contractors: [
                                                                ...brigadiers.map((contractor, i) => {
                                                                    if (index === i) {
                                                                        return {
                                                                            ...contractor,
                                                                            gotPaid: e.target.checked,
                                                                        }
                                                                    }
                                                                    return contractor
                                                                }),
                                                                ...carriers,
                                                            ],
                                                        })
                                                        onValueChanged && onValueChanged(true)
                                                    }}
                                                />
                                            </CustomTableCell>
                                            <CustomTableCell align='right' width={48}>
                                                <Stack spacing={2} direction='row' justifyContent='end'>
                                                    <TableButtonDelete
                                                        nameOfEntityInDialogMessage={row.contractorName}
                                                        onClick={() =>
                                                            setEvent({
                                                                ...event,
                                                                contractors: [
                                                                    ...brigadiers.filter((contractor, i) => {
                                                                        if (index !== i) {
                                                                            return contractor
                                                                        }
                                                                    }),
                                                                    ...carriers,
                                                                ],
                                                            })
                                                        }
                                                    />
                                                </Stack>
                                            </CustomTableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {brigadiersInDifferentEvent.length > 0 ? (
                        <Stack direction='row' spacing={theme.spacing(1)} alignItems='center'>
                            <Typography variant='body2' color={theme.palette.warning.dark}>
                                <WarningAmberOutlinedIcon />
                            </Typography>

                            {
                                isContractorListLoading ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <Typography variant='body2' color={theme.palette.warning.dark}>
                                        {brigadiersInDifferentEvent.map(
                                            (brigadier, index) =>
                                                brigadier && (
                                                    <span key={index}>
                                                        {brigadier.name + ' (' + brigadier.events.map((event) => event.eventName) + ')'}
                                                        {brigadiersInDifferentEvent.length > 1 &&
                                                            brigadiersInDifferentEvent.indexOf(brigadier) !==
                                                            brigadiersInDifferentEvent.length - 1 ? (
                                                            <span>, </span>
                                                        ) : (
                                                            ' '
                                                        )}
                                                    </span>
                                                )
                                        )}
                                        <span>je ve stejné časové období přiřazen/a na jinou událost.</span>
                                    </Typography>
                                )}
                        </Stack>
                    ) : (
                        <></>
                    )}
                    {
                        brigadiers.length > 0 && (
                            <Typography variant='caption'>Upozornění: Nedoporučujeme mazat již schváleného brigádníka.</Typography>
                        )
                    }
                </Stack>
            </Box>
        </>
    )
}

export default BrigadierTable
